import { NOTICE_MAPPING_DEFAVORABLE } from "@etudescarre/constants";
import { Button } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { Plus as PlusIcon, X as XIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api/client";

// "Enregistrer un avis figurant dans un rapport d'examen".
// "Ajouter un avis" (écran 033, "Déposer un rapport d'examen").
//
// Records a single avis (notice) on an existing report: a status picked from
// the emitting contract's company statuses, an optional n° avis (with the
// already-used numbers suggested), and the versions it concerns, chosen
// from the three lists returned by the API (awaiting visa / already visaed /
// other distributed versions).
//
// "Ajouter une observation étayant un avis": the avis being entered can
// carry a first observation (mandatory when the avis is negative, i.e. its
// status maps to "defavorable"), with a sujet de vérification picked among the
// contract's themes or typed freely.
//
// The backend keys everything off report.contractId, so this same form covers
// both cases: transcribing an avis from a received report (écran 022)
// and formulating one's own avis on a report being deposited (écran
// 033) — there the emitting contract is the user's own contract, so statuses
// are "ma société" and versions "accessibles à mon contrat".
//
// This component expects a persisted report to attach the avis to.

type VersionOption = {
  versionId: number;
  documentId: number;
  index: string | null;
  fullname: string | null;
};

type StatusOption = {
  id: number;
  valeurCompany: string;
  mappingChantierCarre: string | null;
};

type ThemeOption = {
  id: number;
  name: string;
};

type NoticeOptions = {
  statuses: StatusOption[];
  versions: {
    awaitingVisa: VersionOption[];
    visaed: VersionOption[];
    otherDiffused: VersionOption[];
  };
  themes: ThemeOption[];
  usedNoticeNumbers: string[];
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "ready"; options: NoticeOptions };

export type NoticeFormProps = {
  yardId: string;
  reportId: number;
  onCreated?: (noticeId: number) => void;
};

const VERSION_GROUPS: {
  key: keyof NoticeOptions["versions"];
  label: string;
}[] = [
  { key: "awaitingVisa", label: "Versions en attente de visa du contrat" },
  { key: "visaed", label: "Versions déjà visées par le contrat" },
  { key: "otherDiffused", label: "Autres versions diffusées au contrat" },
];

function versionLabel(v: VersionOption): string {
  const name = v.fullname ?? `Version ${v.versionId}`;
  return isNil(v.index) ? name : `${name} (ind. ${v.index})`;
}

export function NoticeForm({ yardId, reportId, onCreated }: NoticeFormProps) {
  const [load, setLoad] = useState<LoadState>({ status: "loading" });
  const [statusId, setStatusId] = useState<number | null>(null);
  const [nAvis, setNAvis] = useState("");
  const [selected, setSelected] = useState<VersionOption[]>([]);
  const [obsDescription, setObsDescription] = useState("");
  const [obsThemeId, setObsThemeId] = useState("");
  const [obsNewTheme, setObsNewTheme] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOptions() {
      setLoad({ status: "loading" });
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/reports/{reportId}/notice-options",
          { params: { path: { yardId, reportId: String(reportId) } } },
        );
        if (cancelled) return;
        if (error || isNil(data)) {
          setLoad({
            status: "error",
            error: "Erreur lors du chargement des options de l'avis",
          });
          return;
        }
        setLoad({ status: "ready", options: data.data });
      } catch {
        if (cancelled) return;
        setLoad({
          status: "error",
          error: "Erreur lors du chargement des options de l'avis",
        });
      }
    }

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [yardId, reportId]);

  const addVersion = useCallback((version: VersionOption) => {
    setSelected((prev) =>
      prev.some((v) => v.versionId === version.versionId)
        ? prev
        : [...prev, version],
    );
    setPickerOpen(false);
  }, []);

  const removeVersion = useCallback((versionId: number) => {
    setSelected((prev) => prev.filter((v) => v.versionId !== versionId));
  }, []);

  // The first observation is mandatory when the avis is negative.
  const observationRequired =
    load.status === "ready" &&
    load.options.statuses.find((s) => s.id === statusId)
      ?.mappingChantierCarre === NOTICE_MAPPING_DEFAVORABLE;

  const handleSubmit = useCallback(async () => {
    if (isNil(statusId)) return;
    const description = obsDescription.trim();
    if (observationRequired && description === "") return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const newTheme = obsNewTheme.trim();
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/reports/{reportId}/notices",
        {
          params: { path: { yardId, reportId: String(reportId) } },
          body: {
            statusId,
            nAvis: nAvis.trim() === "" ? undefined : nAvis.trim(),
            versionIds: selected.map((v) => v.versionId),
            ...(description === ""
              ? {}
              : {
                  remark: {
                    description,
                    ...(obsThemeId === ""
                      ? newTheme === ""
                        ? {}
                        : { themeName: newTheme }
                      : { themeId: Number(obsThemeId) }),
                  },
                }),
          },
        },
      );
      if (error || isNil(data)) {
        setSubmitError("Erreur lors de l'enregistrement de l'avis");
        return;
      }
      setStatusId(null);
      setNAvis("");
      setSelected([]);
      setObsDescription("");
      setObsThemeId("");
      setObsNewTheme("");
      onCreated?.(data.data.noticeId);
    } catch {
      setSubmitError("Erreur lors de l'enregistrement de l'avis");
    } finally {
      setSubmitting(false);
    }
  }, [
    yardId,
    reportId,
    statusId,
    nAvis,
    selected,
    obsDescription,
    obsThemeId,
    obsNewTheme,
    observationRequired,
    onCreated,
  ]);

  if (load.status === "loading") {
    return <p className="text-sm text-zinc-500">Chargement...</p>;
  }
  if (load.status === "error") {
    return <p className="text-sm text-red-600">{load.error}</p>;
  }

  const { statuses, versions, themes, usedNoticeNumbers } = load.options;
  const selectedIds = new Set(selected.map((v) => v.versionId));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label
          htmlFor="notice-status"
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300"
        >
          Statut
        </label>
        <select
          id="notice-status"
          required
          className="rounded border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          value={statusId ?? ""}
          onChange={(e) =>
            setStatusId(e.target.value === "" ? null : Number(e.target.value))
          }
        >
          <option value="">Sélectionner un statut</option>
          {statuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.valeurCompany}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="notice-n-avis"
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300"
        >
          N° avis
        </label>
        <input
          id="notice-n-avis"
          type="text"
          maxLength={255}
          list={usedNoticeNumbers.length > 0 ? "notice-n-avis-used" : undefined}
          className="rounded border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          value={nAvis}
          onChange={(e) => setNAvis(e.target.value)}
        />
        {/* Reference list of n° avis already used by the contract. */}
        {usedNoticeNumbers.length > 0 && (
          <>
            <datalist id="notice-n-avis-used">
              {usedNoticeNumbers.map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
            <p className="text-xs text-zinc-500">
              N° déjà utilisés : {usedNoticeNumbers.join(", ")}
            </p>
          </>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Versions concernées
        </span>
        {selected.length > 0 && (
          <ul className="flex flex-col gap-1">
            {selected.map((v) => (
              <li
                key={v.versionId}
                className="flex items-center justify-between rounded bg-zinc-100 px-2 py-1 text-sm dark:bg-zinc-800"
              >
                <span className="truncate" title={versionLabel(v)}>
                  {versionLabel(v)}
                </span>
                <button
                  type="button"
                  aria-label={`Retirer ${versionLabel(v)}`}
                  className="text-zinc-500 hover:text-red-600"
                  onClick={() => removeVersion(v.versionId)}
                >
                  <XIcon size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          aria-label="Ajouter une version"
          className="flex w-fit items-center gap-1 text-sm text-blue-600 hover:underline"
          onClick={() => setPickerOpen((open) => !open)}
        >
          <PlusIcon size={16} /> Associer une version
        </button>

        {pickerOpen && (
          <div className="mt-1 rounded border border-zinc-200 p-2 dark:border-zinc-700">
            {VERSION_GROUPS.map((group) => {
              const available = versions[group.key].filter(
                (v) => !selectedIds.has(v.versionId),
              );
              return (
                <div key={group.key} className="mb-2 last:mb-0">
                  <p className="mb-1 text-xs font-semibold text-zinc-500">
                    {group.label}
                  </p>
                  {available.length === 0 ? (
                    <p className="text-xs text-zinc-400">Aucune version</p>
                  ) : (
                    <ul className="flex flex-col gap-0.5">
                      {available.map((v) => (
                        <li key={v.versionId}>
                          <button
                            type="button"
                            className="w-full truncate text-left text-sm text-zinc-700 hover:text-blue-600 dark:text-zinc-300"
                            title={versionLabel(v)}
                            onClick={() => addVersion(v)}
                          >
                            {versionLabel(v)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="notice-observation"
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300"
        >
          Première observation{" "}
          {observationRequired && <span className="text-red-500">*</span>}
        </label>
        {observationRequired && (
          <p className="text-xs text-zinc-500">
            Une observation est requise pour un avis négatif
          </p>
        )}
        <textarea
          id="notice-observation"
          required={observationRequired}
          maxLength={5000}
          rows={3}
          className="rounded border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          placeholder="Texte de l'observation"
          value={obsDescription}
          onChange={(e) => setObsDescription(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="notice-observation-theme"
          className="text-sm font-medium text-zinc-700 dark:text-zinc-300"
        >
          Sujet de vérification
        </label>
        <select
          id="notice-observation-theme"
          className="rounded border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          value={obsThemeId}
          onChange={(e) => {
            setObsThemeId(e.target.value);
            if (e.target.value !== "") setObsNewTheme("");
          }}
        >
          <option value="">Sélectionner un sujet</option>
          {themes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <input
          id="notice-observation-new-theme"
          type="text"
          aria-label="Nouveau sujet de vérification"
          maxLength={255}
          className="rounded border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          placeholder="Ou saisir un nouveau sujet"
          value={obsNewTheme}
          onChange={(e) => {
            setObsNewTheme(e.target.value);
            if (e.target.value !== "") setObsThemeId("");
          }}
        />
      </div>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}

      <Button
        type="button"
        disabled={
          isNil(statusId) ||
          submitting ||
          (observationRequired && obsDescription.trim() === "")
        }
        onClick={handleSubmit}
      >
        {submitting ? "Enregistrement..." : "Enregistrer l'avis"}
      </Button>
    </div>
  );
}
